import gsap from "gsap";

const prefersReduced = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const NAV_OFFSET = 185;
const NAV_OFFSET_MOBILE = 90;

const scrollingEl = () =>
  document.scrollingElement || document.documentElement;

const maxScrollTop = () => {
  const el = scrollingEl();
  return Math.max(0, el.scrollHeight - el.clientHeight);
};

const resetInnerScrolls = () => {
  document.querySelectorAll(".page, .viewport").forEach((el) => {
    if (el.scrollTop) el.scrollTop = 0;
  });
};

const pendingHash = () =>
  window.__ambidryPendingHash || window.location.hash;

const restorePendingHash = () => {
  const hash = window.__ambidryPendingHash;
  if (!hash) return;
  window.__ambidryPendingHash = "";
  const url = window.location.pathname + window.location.search + hash;
  if (window.location.hash !== hash) {
    history.replaceState(null, "", url);
  }
};

const scrollToY = (top, instant = false) => {
  resetInnerScrolls();
  const el = scrollingEl();
  const y = Math.max(0, Math.min(top, maxScrollTop()));
  if (instant || prefersReduced()) {
    const prev = el.style.scrollBehavior;
    el.style.scrollBehavior = "auto";
    el.scrollTo({ top: y, behavior: "auto" });
    el.style.scrollBehavior = prev;
    return;
  }
  el.scrollTo({ top: y, behavior: "smooth" });
};

const homeUrl = () => {
  const raw = window.ambidryTheme?.homeUrl || "/";
  return raw.endsWith("/") ? raw : `${raw}/`;
};

const normalizePath = (path) => {
  if (!path) return "/";
  const trimmed = path.replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
};

const hrefParts = (href) => {
  const url = new URL(href, window.location.href);
  return {
    path: normalizePath(url.pathname),
    hash: url.hash,
  };
};

const isCurrentPath = (path) =>
  normalizePath(window.location.pathname) === path;

const headingMatch = (pattern) =>
  [
    ...document.querySelectorAll(
      "h1, h2, h3, h4, .brxe-heading, .brxe-text-basic",
    ),
  ].find((el) => pattern.test((el.textContent || "").trim()));

export function markHomepageAnchors() {
  if (!document.body.classList.contains("home")) return;

  const aboutEl = headingMatch(/^about\.?$/i);
  const aboutSection = aboutEl?.closest(".brxe-section, section");
  if (aboutSection && !document.getElementById("about")) {
    aboutSection.id = "about";
  }

  const contactEl = headingMatch(/^contact us$/i);
  const contactSection = contactEl?.closest(".brxe-section, section");
  if (contactSection && !document.getElementById("contact")) {
    contactSection.id = "contact";
  }

}

const rewriteNavLinks = () => {
  const root = homeUrl();
  document
    .querySelectorAll(".nav__link, .nav-overlay__link")
    .forEach((link) => {
      const label = (link.textContent || "").trim().toLowerCase();
      if (label === "about") link.setAttribute("href", `${root}#about`);
      if (label === "contact") link.setAttribute("href", `${root}#contact`);
      link.classList.remove("nav__link--active");
    });
};

const applyNavLogo = () => {
  const src = window.ambidryTheme?.navLogo;
  if (!src) return;
  document.querySelectorAll(".nav__logo img").forEach((img) => {
    img.src = src;
    img.removeAttribute("srcset");
    img.removeAttribute("sizes");
    img.style.width = "auto";
  });
};

export function initNav() {
  const overlay = document.querySelector(".nav-overlay");
  const toggle = document.querySelector(".nav__toggle");
  const closeBtn = overlay?.querySelector(".nav-overlay__close");
  const panel = overlay?.querySelector(".nav-overlay__panel");
  const overlayTop = overlay?.querySelector(".nav-overlay__top");
  const overlayLinks = [
    ...(overlay?.querySelectorAll(".nav-overlay__link") || []),
  ];
  const desktopQuery = window.matchMedia("(min-width: 992px)");
  let menuTl;
  let menuOpen = false;

  // rewriteNavLinks();
  applyNavLogo();

  const hideOverlay = () => {
    if (!overlay || menuOpen) return;
    overlay.hidden = true;
    overlay.classList.remove("is-open");
    document.body.classList.remove("nav-open");
  };

  const createMenuTl = () => {
    if (!overlay || !panel) return null;

    const tl = gsap.timeline({
      paused: true,
      defaults: { ease: "power3.out" },
    });

    tl.fromTo(
      overlay,
      { autoAlpha: 0 },
      { autoAlpha: 1, duration: 0.2, ease: "power2.out" },
      0,
    )
      .fromTo(
        panel,
        { autoAlpha: 0, scale: 0.86, y: -24 },
        {
          autoAlpha: 1,
          scale: 1,
          y: 0,
          duration: 0.5,
          ease: "power3.out",
          transformOrigin: "top right",
        },
        0,
      )
      .fromTo(
        overlayTop,
        { autoAlpha: 0, y: -12 },
        { autoAlpha: 1, y: 0, duration: 0.32 },
        0.16,
      );

    if (closeBtn) {
      tl.fromTo(
        closeBtn,
        { rotate: -90 },
        { rotate: 0, duration: 0.4, ease: "power3.out" },
        0.16,
      );
    }

    tl.fromTo(
      overlayLinks,
      { autoAlpha: 0, y: 20 },
      { autoAlpha: 1, y: 0, duration: 0.36, stagger: 0.07 },
      0.22,
    );

    tl.eventCallback("onReverseComplete", hideOverlay);
    return tl;
  };

  const openMenu = () => {
    if (!overlay || !toggle || desktopQuery.matches) return;
    menuOpen = true;
    overlay.classList.add("is-open");
    overlay.hidden = false;
    toggle.setAttribute("aria-expanded", "true");
    document.body.classList.add("nav-open");

    if (prefersReduced()) {
      menuTl?.pause(0);
      gsap.set([overlay, panel, overlayTop, overlayLinks], {
        autoAlpha: 1,
        y: 0,
        scale: 1,
        clipPath: "none",
      });
      gsap.set(closeBtn, { rotate: 0 });
      return;
    }

    menuTl ??= createMenuTl();
    menuTl?.play();
  };

  const closeMenu = (immediate = false) => {
    if (!overlay || !toggle) return;
    const snapClosed = immediate === true;
    if (!menuOpen && overlay.hidden) return;

    menuOpen = false;
    toggle.setAttribute("aria-expanded", "false");

    if (snapClosed || prefersReduced() || !menuTl) {
      menuTl?.pause(0);
      hideOverlay();
      return;
    }

    menuTl.reverse();
  };

  toggle?.addEventListener("click", () => {
    if (menuOpen) closeMenu();
    else openMenu();
  });

  closeBtn?.addEventListener("click", () => closeMenu());

  overlay?.addEventListener("click", (event) => {
    if (event.target === overlay) closeMenu();
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeMenu();
  });

  desktopQuery.addEventListener("change", (event) => {
    if (event.matches) closeMenu(true);
  });

  const scrollToTop = () => {
    scrollToY(0, prefersReduced());
  };

  const scrollToEl = (target, instant = false) => {
    const top =
      window.scrollY + target.getBoundingClientRect().top - ( desktopQuery.matches ? NAV_OFFSET : NAV_OFFSET_MOBILE );
    scrollToY(top, instant);
  };

  const scrollToHash = (hash, instant = false) => {
    if (!hash || hash === "#") return false;
    if (hash === "#top") {
      scrollToTop();
      return true;
    }
    const id = hash.slice(1);
    let target = document.getElementById(id);
    if (!target) {
      try {
        target = document.querySelector(hash);
      } catch {
        target = null;
      }
    }
    if (!target) return false;
    scrollToEl(target, instant);
    return true;
  };

  markHomepageAnchors();

  if (pendingHash()) {
    history.scrollRestoration = "manual";
  }

  const scrollToLocationHash = (instant = false) => {
    const hash = pendingHash();
    if (!hash) return;
    const go = () => scrollToHash(hash, instant);
    let scrolled = go();
    if (!scrolled && document.body.classList.contains("home")) {
      markHomepageAnchors();
      scrolled = go();
    }
    if (scrolled) restorePendingHash();
  };

  scrollToLocationHash(true);
  window.addEventListener(
    "load",
    () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => scrollToLocationHash(true));
      });
    },
    { once: true },
  );
  window.addEventListener("hashchange", () => scrollToLocationHash());

  document
    .querySelectorAll('.nav a[href*="#"], .nav-overlay a[href*="#"]')
    .forEach((link) => {
      link.addEventListener("click", (event) => {
        const href = link.getAttribute("href");
        if (!href || !href.includes("#")) return;

        let parts;
        try {
          parts = hrefParts(href);
        } catch {
          return;
        }

        if (!parts.hash) return;

        if (!isCurrentPath(parts.path)) {
          closeMenu(true);
          return;
        }

        event.preventDefault();
        closeMenu();
        scrollToHash(parts.hash);

        const next =
          parts.hash === "#top"
            ? window.location.pathname + window.location.search
            : window.location.pathname + window.location.search + parts.hash;
        if (next !== window.location.pathname + window.location.search + window.location.hash) {
          history.pushState(null, "", next);
        }
      });
    });

  const updateNavScrolled = () => {
    document.body.classList.toggle("nav-scrolled", window.scrollY > 12);
  };

  window.addEventListener("scroll", updateNavScrolled, { passive: true });
  document.addEventListener("scroll", updateNavScrolled, { passive: true });
  window.addEventListener("resize", updateNavScrolled);
  updateNavScrolled();
}
